/**
 * Chat API endpoints
 */

import { post, streamChat } from './client';
import type { ActionPreview } from '../types';

interface ConfirmActionResponse {
  success: boolean;
  result?: {
    message?: string;
    person?: {
      name: string;
      type_label: string;
    };
    error?: string;
  };
  error?: string;
}

/**
 * Send a message and stream the response
 *
 * @param sessionUuid - Session UUID
 * @param message - User message
 * @param callbacks - Event callbacks
 * @returns Cleanup function to abort
 */
export function sendMessage(
  sessionUuid: string,
  message: string,
  callbacks: {
    onContent?: (content: string) => void;
    onToolStart?: (toolName: string, toolId: string) => void;
    onToolResult?: (toolId: string, result: Record<string, unknown>) => void;
    onActionPreview?: (preview: ActionPreview) => void;
    onDone?: (title?: string) => void;
    onError?: (error: string) => void;
  }
): () => void {
  return streamChat(
    `/api/chat/${sessionUuid}`,
    { message },
    (event) => {
      try {
        const data = JSON.parse(event.data);

        switch (data.type) {
          case 'content':
            callbacks.onContent?.(data.content || '');
            break;

          case 'tool_start':
            callbacks.onToolStart?.(data.tool_name, data.tool_id);
            break;

          case 'tool_result':
            callbacks.onToolResult?.(data.tool_id, data.result);
            break;

          case 'action_preview':
            callbacks.onActionPreview?.(data.preview);
            break;

          case 'done':
            callbacks.onDone?.(data.title);
            break;

          case 'error':
            callbacks.onError?.(data.error || 'Unknown error');
            break;
        }
      } catch {
        // Non-JSON content, treat as text
        callbacks.onContent?.(event.data);
      }
    },
    (error) => {
      callbacks.onError?.(error.message);
    }
  );
}

/**
 * Confirm an action (sacuvaj_osobu, etc.)
 *
 * This bypasses the LLM and directly executes the tool with confirm=true
 */
export async function confirmAction(
  sessionUuid: string,
  tool: string,
  args: Record<string, unknown>
): Promise<ConfirmActionResponse> {
  return post<ConfirmActionResponse>('/api/confirm-action', {
    session_uuid: sessionUuid,
    tool,
    arguments: args,
  });
}

/**
 * Submit feedback (thumbs up/down) for a message
 */
export async function submitFeedback(
  messageId: number,
  sessionUuid: string,
  feedbackType: 'up' | 'down'
): Promise<{ success: boolean; feedback_type?: string; error?: string }> {
  return post('/api/feedback', {
    message_id: messageId,
    session_uuid: sessionUuid,
    feedback_type: feedbackType,
  });
}

/**
 * Get all feedback for a session
 */
export async function getSessionFeedback(
  sessionUuid: string
): Promise<{ feedback: Record<number, 'up' | 'down'> }> {
  const response = await fetch(`/api/feedback/${sessionUuid}`, {
    credentials: 'include',
  });
  return response.json();
}

/**
 * Remove feedback for a message
 */
export async function deleteFeedback(
  messageId: number
): Promise<{ success: boolean }> {
  const response = await fetch(`/api/feedback/${messageId}`, {
    method: 'DELETE',
    credentials: 'include',
  });
  return response.json();
}
